import type { Book, Ref, Translation } from './types';

export interface SearchResult {
  ref: Ref;
  book: Book;
  translation: Translation;
  label: string;
  score: number;
}

/** Conventional abbreviations that a subsequence match alone would rank poorly. */
const ALIASES: Record<string, string> = {
  gen: 'genesis',
  ge: 'genesis',
  gn: 'genesis',
  ex: 'exodus',
  exo: 'exodus',
  exod: 'exodus',
  lev: 'leviticus',
  lv: 'leviticus',
  num: 'numbers',
  nu: 'numbers',
  nm: 'numbers',
  deut: 'deuteronomy',
  deu: 'deuteronomy',
  dt: 'deuteronomy',
  josh: 'joshua',
  jos: 'joshua',
  judg: 'judges',
  jdg: 'judges',
  jgs: 'judges',
  ru: 'ruth',
  rth: 'ruth',
  '1sam': '1-samuel',
  '1sa': '1-samuel',
  '1sm': '1-samuel',
  '2sam': '2-samuel',
  '2sa': '2-samuel',
  '2sm': '2-samuel',
  '1kgs': '1-kings',
  '1ki': '1-kings',
  '1kg': '1-kings',
  '2kgs': '2-kings',
  '2ki': '2-kings',
  '2kg': '2-kings',
  '1chr': '1-chronicles',
  '1ch': '1-chronicles',
  '2chr': '2-chronicles',
  '2ch': '2-chronicles',
  ezr: 'ezra',
  neh: 'nehemiah',
  est: 'esther',
  esth: 'esther',
  jb: 'job',
  ps: 'psalms',
  psa: 'psalms',
  psalm: 'psalms',
  pss: 'psalms',
  psm: 'psalms',
  prov: 'proverbs',
  pr: 'proverbs',
  prv: 'proverbs',
  eccl: 'ecclesiastes',
  ecc: 'ecclesiastes',
  qoh: 'ecclesiastes',
  song: 'song-of-solomon',
  songofsongs: 'song-of-solomon',
  sos: 'song-of-solomon',
  ss: 'song-of-solomon',
  canticles: 'song-of-solomon',
  cant: 'song-of-solomon',
  isa: 'isaiah',
  is: 'isaiah',
  jer: 'jeremiah',
  je: 'jeremiah',
  lam: 'lamentations',
  ezek: 'ezekiel',
  eze: 'ezekiel',
  ezk: 'ezekiel',
  dan: 'daniel',
  dn: 'daniel',
  hos: 'hosea',
  jl: 'joel',
  am: 'amos',
  obad: 'obadiah',
  ob: 'obadiah',
  jon: 'jonah',
  jnh: 'jonah',
  mic: 'micah',
  mi: 'micah',
  nah: 'nahum',
  na: 'nahum',
  hab: 'habakkuk',
  zeph: 'zephaniah',
  zep: 'zephaniah',
  hag: 'haggai',
  hg: 'haggai',
  zech: 'zechariah',
  zec: 'zechariah',
  mal: 'malachi',
  matt: 'matthew',
  mat: 'matthew',
  mt: 'matthew',
  mk: 'mark',
  mr: 'mark',
  mrk: 'mark',
  lk: 'luke',
  luk: 'luke',
  jn: 'john',
  jhn: 'john',
  ac: 'acts',
  act: 'acts',
  rom: 'romans',
  rm: 'romans',
  '1cor': '1-corinthians',
  '1co': '1-corinthians',
  '2cor': '2-corinthians',
  '2co': '2-corinthians',
  gal: 'galatians',
  ga: 'galatians',
  eph: 'ephesians',
  phil: 'philippians',
  php: 'philippians',
  pp: 'philippians',
  col: 'colossians',
  '1thess': '1-thessalonians',
  '1th': '1-thessalonians',
  '2thess': '2-thessalonians',
  '2th': '2-thessalonians',
  '1tim': '1-timothy',
  '1ti': '1-timothy',
  '2tim': '2-timothy',
  '2ti': '2-timothy',
  tit: 'titus',
  ti: 'titus',
  phlm: 'philemon',
  philem: 'philemon',
  phm: 'philemon',
  heb: 'hebrews',
  jas: 'james',
  jam: 'james',
  jms: 'james',
  '1pet': '1-peter',
  '1pe': '1-peter',
  '1pt': '1-peter',
  '2pet': '2-peter',
  '2pe': '2-peter',
  '2pt': '2-peter',
  '1jn': '1-john',
  '1jo': '1-john',
  '1joh': '1-john',
  '2jn': '2-john',
  '2jo': '2-john',
  '2joh': '2-john',
  '3jn': '3-john',
  '3jo': '3-john',
  '3joh': '3-john',
  jud: 'jude',
  jde: 'jude',
  rev: 'revelation',
  rv: 'revelation',
  apoc: 'revelation',
};

interface Indexed {
  book: Book;
  compact: string;
  wordStarts: Set<number>;
}

const indexCache = new WeakMap<Book[], Indexed[]>();

function indexBooks(books: Book[]): Indexed[] {
  let index = indexCache.get(books);
  if (index) return index;

  index = books.map((book) => {
    let compact = '';
    const wordStarts = new Set<number>();
    let atBoundary = true;
    for (const ch of book.slug) {
      if (ch === '-') {
        atBoundary = true;
        continue;
      }
      if (atBoundary) wordStarts.add(compact.length);
      compact += ch;
      atBoundary = false;
    }
    return { book, compact, wordStarts };
  });

  indexCache.set(books, index);
  return index;
}

/** Subsequence match, rewarding prefixes, word starts and runs. -1 if no match. */
function fuzzyScore(query: string, entry: Indexed): number {
  const { compact, wordStarts } = entry;
  if (query.length > compact.length) return -1;

  let score = 0;
  let qi = 0;
  let prev = -2;

  for (let i = 0; i < compact.length && qi < query.length; i++) {
    if (compact[i] !== query[qi]) continue;
    if (i === 0) score += 16;
    else if (wordStarts.has(i)) score += 12;
    else if (i === prev + 1) score += 8;
    else score += 2;
    prev = i;
    qi++;
  }
  if (qi < query.length) return -1;

  // Favour tight matches: "john" over "john" buried in a longer name.
  score += Math.round((query.length / compact.length) * 24);
  if (compact === query) score += 200;
  else if (compact.startsWith(query)) score += 60;
  if (ALIASES[query] === entry.book.slug) score += 150;

  return score;
}

/** Matches a translation by exact id/abbr or by a prefix of at least 2 chars. */
function matchTranslation(
  token: string,
  translations: Translation[],
): { translation: Translation; score: number; exact: boolean } | null {
  for (const t of translations) {
    if (token === t.id) return { translation: t, score: 220, exact: true };
  }
  if (token.length < 2) return null;
  for (const t of translations) {
    if (t.id.startsWith(token)) {
      return {
        translation: t,
        score: 100 + Math.round((token.length / t.id.length) * 40),
        exact: false,
      };
    }
  }
  return null;
}

const label = (book: Book, chapter: number, verse?: number) =>
  `${book.name} ${chapter}${verse ? `:${verse}` : ''}`;

interface Ctx {
  books: Book[];
  translations: Translation[];
  /** The chapter the palette was opened from — what a bare "ESV" should reuse. */
  context: Ref;
}

/** One reading of the query: an optional translation plus the leftover book text. */
interface Interpretation {
  text: string;
  translation?: Translation;
  translationScore: number;
  /** True when the translation was named in full, which licenses a much
   *  stronger assumption that the rest of the query is a book. */
  exact: boolean;
}

export function search(query: string, ctx: Ctx, limit = 8): SearchResult[] {
  const { books, translations, context } = ctx;
  const byId = (id: string) => translations.find((t) => t.id === id)!;
  const bookById = (id: number) => books.find((b) => b.id === id)!;

  const q = query.trim().toLowerCase().replace(/[.\s]+/g, ' ');

  // Nothing typed: offer the same chapter in every other translation, which is
  // the overwhelmingly common reason to open this from the "add column" button.
  if (!q) {
    const current = bookById(context.book);
    return translations
      .filter((t) => t.id !== context.translation)
      .map((t, i) => ({
        ref: { translation: t.id, book: context.book, chapter: context.chapter },
        book: current,
        translation: t,
        label: label(current, context.chapter),
        score: 100 - i,
      }));
  }

  const tokens = q.split(' ');
  const interpretations: Interpretation[] = [
    { text: q, translationScore: 0, exact: false },
  ];

  const push = (token: string, rest: string) => {
    const hit = matchTranslation(token, translations);
    if (hit) {
      interpretations.push({
        text: rest,
        translation: hit.translation,
        translationScore: hit.score,
        exact: hit.exact,
      });
    }
  };

  if (tokens.length === 1) {
    push(tokens[0], '');
    // Also split a translation glued to the reference, e.g. "rom8niv".
    // A leftover of one character is noise ("nkjv" is not "n" in the KJV),
    // so only split when something book-shaped remains.
    const token = tokens[0];
    for (const t of translations) {
      const rest = token.endsWith(t.id)
        ? token.slice(0, -t.id.length)
        : token.startsWith(t.id)
        ? token.slice(t.id.length)
        : null;
      if (rest && rest.length >= 2) {
        interpretations.push({
          text: rest,
          translation: t,
          translationScore: 220,
          exact: true,
        });
      }
    }
  } else {
    push(tokens[tokens.length - 1], tokens.slice(0, -1).join(' '));
    push(tokens[0], tokens.slice(1).join(' '));
  }

  const results = new Map<string, SearchResult>();
  const add = (result: SearchResult) => {
    const key = `${result.ref.translation}.${result.ref.book}.${result.ref.chapter}.${
      result.ref.verse ?? ''
    }`;
    const existing = results.get(key);
    if (!existing || existing.score < result.score) results.set(key, result);
  };

  const index = indexBooks(books);

  for (const interp of interpretations) {
    // Split a trailing "3" or "3:16" off the book text.
    const withRef = interp.text.match(/^(.*?)\s*(\d+)(?::(\d+))?$/);
    const hasLetters = /[a-z]/.test(withRef?.[1] ?? '');

    let bookText = interp.text;
    let chapter: number | undefined;
    let verse: number | undefined;

    if (withRef && (hasLetters || withRef[1] === '')) {
      bookText = withRef[1];
      chapter = Number(withRef[2]);
      verse = withRef[3] ? Number(withRef[3]) : undefined;
    }

    // Book names are indexed without separators, so "1 cor" and "1cor" both
    // have to reduce to the same needle.
    bookText = bookText.replace(/[\s-]/g, '');

    const translation = interp.translation ?? byId(context.translation);

    // No book named — reuse the column we came from. This is what makes typing
    // a bare "ESV" (or a bare "12") resolve against the neighbouring column.
    if (!bookText) {
      if (!interp.translation && chapter === undefined) continue;
      const book = bookById(context.book);
      const target = Math.min(chapter ?? context.chapter, book.chapters);
      add({
        ref: { translation: translation.id, book: book.id, chapter: target, verse },
        book,
        translation,
        label: label(book, target, verse),
        // A fully-named translation is unambiguous; a two-letter prefix is a
        // guess that shouldn't outrank a book the reader clearly named.
        score: (!interp.translation || interp.exact ? 400 : 60) +
          interp.translationScore + (chapter !== undefined ? 10 : 0),
      });
      continue;
    }

    for (const entry of index) {
      const bookScore = fuzzyScore(bookText, entry);
      if (bookScore < 0) continue;

      const requested = chapter ?? 1;
      const target = Math.min(requested, entry.book.chapters);
      add({
        ref: { translation: translation.id, book: entry.book.id, chapter: target, verse },
        book: entry.book,
        translation,
        label: label(entry.book, target, verse),
        // A chapter number the book doesn't have is a weak signal, not a reason
        // to hide the book entirely.
        score: bookScore + interp.translationScore + (requested > target ? -40 : 0),
      });
    }
  }

  return [...results.values()].sort((a, b) => b.score - a.score).slice(0, limit);
}
